<template>
  <div
    ref="root"
    class="xp-window xp-font"
    :class="{ inactive: !active && !maximized, 'pointer-events-none': minimized, 'xp-maximized': maximized }"
    :style="
      maximized
        ? { opacity: 1, zIndex: 60 }
        : {
            width: `${width}px`,
            maxWidth: '96vw',
            opacity: minimized ? 0 : 1,
            transform: `translate(${pos.x}px, ${pos.y}px) scale(${minimized ? 0.6 : active ? 1 : 0.88})`,
            filter: active ? 'none' : 'brightness(0.82)',
            zIndex: active ? 20 : 10,
          }
    "
  >
    <div
      class="xp-titlebar"
      style="cursor: move"
      @pointerdown="onTitlePointerDown"
      @dblclick="onTitleDblClick"
    >
      <span v-if="icon" class="text-sm">{{ icon }}</span>
      <span class="truncate">{{ title }}</span>
      <div class="xp-title-buttons" @click.stop>
        <button
          class="xp-title-btn"
          :aria-label="`${title} minimize`"
          title="Minimize"
          @click="$emit('minimize')"
        >
          _
        </button>
        <button
          class="xp-title-btn"
          :aria-label="`${title} maximize`"
          title="Maximize"
          @click="$emit('maximize')"
        >
          ▢
        </button>
        <button
          class="xp-title-btn close"
          :aria-label="`${title} close`"
          title="Close"
          @click="$emit('close')"
        >
          ✕
        </button>
      </div>
    </div>
    <div v-if="menubar" class="xp-font xp-menubar-sep flex gap-3 bg-[var(--xp-face)] px-2 py-1 text-[12px] text-black">
      <span v-for="m in menubar" :key="m" class="xp-menubar-item cursor-default px-1">
        {{ m }}
      </span>
    </div>
    <div
      class="xp-window-body"
      :style="
        maximized
          ? undefined
          : bodyHeight
            ? { height: `${bodyHeight}px`, overflowY: 'auto' }
            : undefined
      "
    >
      <slot />
    </div>
    <!-- Resize handle (bottom-right) -->
    <div
      class="xp-resize-handle"
      role="separator"
      aria-orientation="horizontal"
      :aria-label="`Resize ${title}`"
      @pointerdown="onResizeStart"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

const props = withDefaults(
  defineProps<{
    title: string
    icon?: string
    active: boolean
    minimized: boolean
    maximized?: boolean
    menubar?: string[]
    width?: number
    bodyHeight?: number | null
  }>(),
  { width: 620, bodyHeight: null, maximized: false },
)

const emit = defineEmits<{
  minimize: []
  maximize: []
  close: []
  focus: []
  resize: [size: { w: number; h: number | null }]
}>()

const root = ref<HTMLElement | null>(null)

/** Drag offset (px) applied on top of the cascade position. */
const pos = ref({ x: 0, y: 0 })

function onTitlePointerDown(e: PointerEvent): void {
  if ((e.target as HTMLElement).closest('.xp-title-buttons')) return
  emit('focus')
  if (e.button !== 0 || props.maximized) return
  e.preventDefault()
  const el = root.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  const grabX = e.clientX - rect.left
  const grabY = e.clientY - rect.top
  const start = { ...pos.value }
  let active = true
  const move = (ev: PointerEvent): void => {
    if (!active) return
    const dx = ev.clientX - grabX - rect.left
    const dy = ev.clientY - grabY - rect.top
    const minDx = -(rect.width - 120) - rect.left
    const maxDx = window.innerWidth - 120 - rect.left
    const minDy = -rect.top
    const maxDy = window.innerHeight - 48 - rect.top
    pos.value = {
      x: start.x + Math.min(Math.max(dx, minDx), maxDx),
      y: start.y + Math.min(Math.max(dy, minDy), maxDy),
    }
  }
  const up = (): void => {
    active = false
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up)
  }
  window.addEventListener('pointermove', move)
  window.addEventListener('pointerup', up)
}

function onTitleDblClick(e: MouseEvent): void {
  if ((e.target as HTMLElement).closest('.xp-title-buttons')) return
  emit('maximize')
}

let resizing = false
let startX = 0
let startY = 0
let startW = 0
let startH = 0
let raf = 0

function onResizeStart(e: PointerEvent): void {
  e.preventDefault()
  e.stopPropagation()
  resizing = true
  startX = e.clientX
  startY = e.clientY
  startW = props.width
  startH = props.bodyHeight ?? root.value?.querySelector('.xp-window-body')?.clientHeight ?? 400
  ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  const move = (ev: PointerEvent): void => {
    if (!resizing) return
    cancelAnimationFrame(raf)
    const dw = ev.clientX - startX
    const dh = ev.clientY - startY
    raf = requestAnimationFrame(() => {
      const maxW = window.innerWidth - 32
      const maxH = window.innerHeight - 120
      const w = Math.min(Math.max(360, startW + dw), maxW)
      const h = Math.min(Math.max(220, startH + dh), maxH)
      emit('resize', { w: Math.round(w), h: Math.round(h) })
    })
  }
  const up = (): void => {
    resizing = false
    cancelAnimationFrame(raf)
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up)
  }
  window.addEventListener('pointermove', move)
  window.addEventListener('pointerup', up)
}
</script>

<style scoped>
.xp-resize-handle {
  position: absolute;
  right: 2px;
  bottom: 2px;
  width: 18px;
  height: 18px;
  cursor: nwse-resize;
  background:
    linear-gradient(135deg, transparent 50%, #ffffff 50%, #ffffff 55%, transparent 55%),
    linear-gradient(135deg, transparent 62%, #7f7f7f 62%, #7f7f7f 67%, transparent 67%),
    linear-gradient(135deg, transparent 74%, #ffffff 74%, #ffffff 79%, transparent 79%);
}
</style>
